
























































































































































































































































import Vue from "vue";
import { computed, ref } from "@vue/composition-api";
import Icon from "@/components/Icon.vue";
import GaIcon from "./GaIcon.vue";
import GenAiCard from "./GenAiCard.vue";
import GenAiDonut from "./GenAiDonut.vue";
import ViewModeToggle from "./ViewModeToggle.vue";
import SkeletonLoaderItem from "@/components/SkeletonLoaderItem.vue";
import { LOAD_FAILED_TEXT, state } from "../data/state";
import {
  AiFileActivityId,
  INFO_CATEGORIES,
  InfoCategoryId,
  LevelBreakdown,
  by_relation,
  describe_levels,
} from "../data/model";
import { SIZE, TYPE, useGenAiPalette } from "../data/design";
import { show_gen_ai_category_events } from "../data/drilldown";
import { risk_share_line } from "../data/share";
import { use_view_mode } from "../data/view-mode";

/** risk levels only — see the legend comment in the template */
const SEVERITY_KEYS: (keyof LevelBreakdown)[] = ["high", "medium", "low"];

const TONE_LABEL: Record<string, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

/** the drill-down speaks the app's numeric severity encoding */
const LEVEL_BY_TONE: Record<string, number> = {
  high: 3,
  medium: 2,
  low: 1,
};

/** gen-ai-ui's activity glyphs (metadata/activityTypes.json) */
const GLYPH: Record<string, string> = {
  created: "sparkles",
  modified: "pencil",
  exposed: "eye",
};

type RelationRow = {
  id: AiFileActivityId;
  name: string;
  icon: string;
  severities: LevelBreakdown;
  count: number;
};

/**
 * Widget 2 — "Risky AI Agent Activities" (gen-ai-ui, Sep 2026).
 *
 * AI -> content only: what AI agents generated, modified or read. The
 * employee -> AI side is SharedByEmployeesCard. Rows are the three
 * AI-originated activities, each split by risk severity, narrowed to one
 * information category or summed across all of them.
 */
export default Vue.extend({
  components: {
    Icon,
    GaIcon,
    GenAiCard,
    GenAiDonut,
    ViewModeToggle,
    SkeletonLoaderItem,
  },
  setup() {
    const palette = useGenAiPalette();
    const view = use_view_mode("riskBySeverity");
    const is_loading = computed(() => state.loading.value.is_loading);

    /** "all" means unfiltered; tapping the active chip returns to it */
    const active_category = ref<InfoCategoryId | "all">("all");
    const categories = INFO_CATEGORIES;
    const toggle_category = (id: InfoCategoryId) => {
      active_category.value = active_category.value === id ? "all" : id;
    };

    /** risk counts exclude "none" — a no-risk activity is not a risky one */
    const items = computed<RelationRow[]>(() =>
      by_relation(state.overview.value.risk_cube, active_category.value, "ai").map(
        (row) => ({
          ...row,
          count: SEVERITY_KEYS.reduce((sum, k) => sum + row.severities[k], 0),
        })
      )
    );

    const total = computed(() =>
      items.value.reduce((sum, d) => sum + d.count, 0)
    );

    /** the busiest activity fills its track; the rest are drawn against it */
    const max_value = computed(() =>
      Math.max(1, ...items.value.map((d) => d.count))
    );

    const pct = (item: RelationRow) =>
      total.value ? Math.round((100 * item.count) / total.value) : 0;

    const high_count = computed(() =>
      items.value.reduce((sum, d) => sum + d.severities.high, 0)
    );

    const review_text = computed(() =>
      high_count.value === 1
        ? "1 high-severity activity needs review."
        : `${high_count.value.toLocaleString()} high-severity activities need review.`
    );

    /** a failed request has no count: a dash, never a zero */
    const load_failed = computed(() => state.failed.value.agents_risk);

    const category_name = computed(() => {
      const found = categories.find((c) => c.id === active_category.value);
      return found ? found.name : "";
    });

    const subtitle = computed(() =>
      risk_share_line(
        state.overview.value.shares,
        "ai",
        total.value,
        SEVERITY_KEYS
      )
    );

    /** the label has to say what the click will do, since the number alone
     *  cannot convey which filters travel with it */
    const aria_for = (item: RelationRow) =>
      `Open ${item.count} ${item.name} activities` +
      (active_category.value === "all"
        ? ""
        : ` — ${category_name.value} information`) +
      `, ${pct(item)}% of risky AI agent activity. By severity: ` +
      `${describe_levels(item.severities, SEVERITY_KEYS)}.`;

    const donut_items = computed(() =>
      items.value.map((item) => ({
        label: item.name,
        value: item.count,
        color: palette.value.activityChart[item.id],
      }))
    );

    const open_events = (
      relation?: { id: AiFileActivityId; name: string },
      severities?: string[]
    ) =>
      show_gen_ai_category_events(
        {
          name: active_category.value === "all" ? "all" : category_name.value,
        },
        {
          widget: "severity",
          // a log of what the AI agent did, so it opens as AI activities
          ai_activities: true,
          direction: "ai",
          relation,
          all_categories: active_category.value === "all",
          category_id:
            active_category.value === "all" ? undefined : active_category.value,
          severities: (severities || SEVERITY_KEYS).map((k) => LEVEL_BY_TONE[k]),
        }
      );

    const show_events = (item?: RelationRow) =>
      item && open_events({ id: item.id, name: item.name });

    const show_high_severity = () => open_events(undefined, ["high"]);

    return {
      view,
      is_loading,
      items,
      total,
      max_value,
      pct,
      high_count,
      review_text,
      load_failed,
      LOAD_FAILED_TEXT,
      subtitle,
      categories,
      active_category,
      toggle_category,
      aria_for,
      donut_items,
      show_events,
      show_high_severity,
      SEVERITY_KEYS,
      TONE_LABEL,
      GLYPH,
      T: palette,
      TY: TYPE,
      S: SIZE,
    };
  },
});
